import update from 'immutability-helper';
import { IAppState, IFormControl } from '../types';
import {
    IFieldSetSettings,
    IFormSettings,
    FormSettingsControls,
    isFormStep,
    isFieldSet,
    hasFormStep,
} from '../types/IFormSettings';
import specificValidations from './validations/specificValidations';
import { checkConditionalControls, checkConditionalFieldSets } from './controlCondition.helper';
import { hasValue } from './validation.helper';

const getStep = (length: number) => (length - 1 > -1 ? length - 1 : 0);

export const mapControls = (
    target: Partial<IAppState>,
    props: IFormControl,
    parentProps: IFieldSetSettings | IFormControl | undefined,
    inStep: number,
) => {
    const formTargetState = target.controlsState!;
    formTargetState[props.controlName] = {
        ...props,
        disabled: props.disabled ?? !props.visible,
        value: props.value ?? '',
        parentProps,
        inStep,
        formMeta: Object.freeze(target.formMeta),
        specificValidation:
            props.controlName in specificValidations
                ? specificValidations[props.controlName]
                : undefined,
    }

    if (props.condition) {
        target.conditionalControlNames!.push(props.controlName);
    }

    return target;
};

export const mapFieldSet = (
    target: Partial<IAppState>,
    props: IFieldSetSettings
) => {
    const formTargetState = target.fieldSetState!;

    formTargetState[props.fieldSetName] = {
        ...props,
    }

    if (props.condition) {
        target.conditionalFieldSets!.push(props.fieldSetName);
    }

    return target;
};

const mapFieldSetAndControls = (
    target: Partial<IAppState>,
    props: FormSettingsControls,
    progressRatio: number[],
): any => {
    if (isFormStep(props)) {
        progressRatio.push(0);
        const stepControls = props.controls.reduce(
            (dest, control) => mapFieldSetAndControls(dest, control, progressRatio),
            target,
        );
        return stepControls;
    }

    if (isFieldSet(props)) {
        mapFieldSet(target, props);

        const fieldsetReduced = props.controls.reduce(
            (_, p) => {
                return mapControls(target, p, props, getStep(progressRatio.length))
            },
            target,
        );
        return fieldsetReduced;
    }

    return mapControls(target, props, undefined, getStep(progressRatio.length));
};

/**
 * @description calculates the progress ratio based on required controlNames (and fieldSet's visibility) in state
 * @param {IAppState} appState
 * @param {number} currentStep
 */
export const getProgressRatio = (appState: IAppState) => {
    const { controlsState, fieldSetState, step } = appState;

    const { filled, total } = Object.values(controlsState).reduce(
        (obj: { filled: number; total: number }, control: IFormControl) => {

            let parentIsVisible = true;
            if (control && control?.parentProps && isFieldSet(control.parentProps)) {
                const fieldSetName = control.parentProps.fieldSetName;
                parentIsVisible = fieldSetState[fieldSetName].visible ?? true;
            }

            if (!control.required || control.inStep !== step || !control.visible || !parentIsVisible) {
                return obj;
            }

            return {
                filled: obj.filled + (hasValue(control.type, control.value) ? 1 : 0),
                total: obj.total + 1,
            };
        },
        { filled: 0, total: 0 },
    );

    if (!filled && !total) return 1; // Can happen if no inputs are required in the step.
    return filled / total;
};

export const prepareStore = (settings: IFormSettings) => {
    const progressRatio = [] as number[];

    const initialValues = {
        conditionalControlNames: [],
        conditionalFieldSets: [],
        controlsState: {},
        fieldSetState: {},
        formMeta: Object.freeze(settings.formMeta),
        initialSettings: Object.freeze(settings),
        progressRatio,
        step: hasFormStep(settings) ? 0 : undefined,
    } as Partial<IAppState>;

    const temp = settings.controls.reduce(
        (target, props) => mapFieldSetAndControls(target, props, progressRatio),
        initialValues,
    ) as unknown;

    const appState = temp as IAppState;
    const condControlsSpec = checkConditionalControls(appState, {});
    const condFieldSetsSpec = checkConditionalFieldSets(appState, {});

    progressRatio[0] = getProgressRatio(appState);

    appState.controlsState = update(appState.controlsState, condControlsSpec);
    appState.fieldSetState = update(appState.fieldSetState, condFieldSetsSpec);

    return appState;
};
